/**
 * Block Generator Database Layer
 *
 * Handles all API calls for audio block generation admin operations.
 * Mirrors the pattern from VPR/utils/vprDatabase.js.
 */

import { getAuth } from 'firebase/auth';

const BLOCK_GENERATOR_URL =
  'https://us-central1-guidesofia-access-control.cloudfunctions.net/blockGeneratorAdmin';

async function callBlockAPI(endpoint, method = 'GET', data = null) {
  const headers = { 'Content-Type': 'application/json' };

  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  } else if (method !== 'GET') {
    throw new Error('Authentication required. Please log in.');
  }

  const url = `${BLOCK_GENERATOR_URL}${endpoint}`;
  const options = { method, headers };
  if (data && method !== 'GET') options.body = JSON.stringify(data);

  const response = await fetch(url, options);

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch (_e) {}
    throw new Error(errorMessage);
  }

  return response.json();
}

// ============================================================================
// BLOCK GENERATOR OPERATIONS
// ============================================================================

/**
 * Suggest relevant audio block topics for a POI based on its description
 * and/or comprehensive context document.
 *
 * @param {string} poiName
 * @param {string} [poiInDepthDescription]
 * @param {string} [comprehensiveDocUrl]
 */
export async function suggestTopicsForPoi(poiName, poiInDepthDescription, comprehensiveDocUrl) {
  return callBlockAPI('/suggest-topics', 'POST', {
    poiName,
    poiInDepthDescription,
    comprehensiveDocUrl,
  });
}

/**
 * Suggest topics for multiple POIs in a single LLM call.
 *
 * @param {Array<{ poiName: string, poiInDepthDescription: string }>} pois
 * @param {string} [comprehensiveDocUrl]
 * @returns {Promise<{ results: Array<{ poiName: string, topics: string[] }> }>}
 */
export async function consolidateTopicsForPoi(poiName, topics) {
  return callBlockAPI('/consolidate-topics', 'POST', { poiName, topics });
}

/**
 * Analyze generated blocks across all POIs and flag redundant ones.
 *
 * @param {Array<{ id: string, poiName: string, topic: string, type: string, transcript: string }>} blocks
 * @returns {Promise<{ results: Array<{ blockId: string, verdict: string, reason: string|null }> }>}
 */
export async function consolidateBlocks(blocks) {
  return callBlockAPI('/consolidate-blocks', 'POST', { blocks });
}

export async function suggestTopicsForPoiBatch(pois, comprehensiveDocUrl) {
  return callBlockAPI('/suggest-topics-batch', 'POST', {
    pois,
    comprehensiveDocUrl,
  });
}

/**
 * Generate mandatory + topic audio blocks for a single POI.
 *
 * @param {string}   itineraryId            - The itinerary identifier
 * @param {string}   poiId                  - Unique POI identifier
 * @param {string}   poiName                - Human-readable POI name
 * @param {string[]} topics                 - Topic names (e.g. ['history', 'architecture'])
 * @param {string[]} languages              - Language codes (e.g. ['en', 'pt'])
 * @param {string}   [depth]                - 'quick' | 'standard' | 'detailed'
 * @param {string}   [tone]                 - 'storytelling' | 'casual' | 'enthusiastic'
 * @param {string}   [poiInDepthDescription] - Source text for mandatory blocks
 * @param {number}   [mandatoryBlockCount]  - Number of mandatory blocks (default 2)
 * @param {string}   [ragManifestUrl]       - RAG manifest URL for topic enrichment
 * @param {string}   [comprehensiveDocUrl]  - URL to comprehensive context document JSON
 * @param {boolean}  [generateSound]        - Whether to generate TTS audio (default true)
 * @param {boolean}  [useSimpleTts]         - Use SimpleTTS REST API (higher quality)
 * @param {string}   [voiceModel]           - TTS model (e.g. 'gemini-2.5-pro-preview-tts')
 */
export async function generateBlocksForPoi(
  itineraryId,
  poiId,
  poiName,
  topics,
  languages,
  depth = 'standard',
  tone = 'storytelling',
  poiInDepthDescription,
  mandatoryBlockCount = 2,
  ragManifestUrl,
  model = 'gpt-5',
  voiceName = 'Aoede',
  comprehensiveDocUrl,
  generateSound = true,
  useSimpleTts = false,
  voiceModel
) {
  return callBlockAPI('/generate-blocks', 'POST', {
    itineraryId,
    poiId,
    poiName,
    topics,
    languages,
    depth,
    tone,
    poiInDepthDescription,
    mandatoryBlockCount,
    ragManifestUrl,
    comprehensiveDocUrl,
    model,
    voiceName,
    generateSound,
    useSimpleTts,
    voiceModel,
  });
}

/**
 * Generate all blocks for a POI in one LLM call (blocks-first approach).
 * Returns text only — no audio. Use generateAudioForBlock() separately.
 *
 * @param {string}   itineraryId
 * @param {string}   poiId
 * @param {string}   poiName
 * @param {string[]} languages
 * @param {string}   [depth]
 * @param {string}   [tone]
 * @param {string}   [poiInDepthDescription]
 * @param {string}   [comprehensiveDocUrl]
 * @param {string}   [ragManifestUrl]
 * @param {number}   [blockCount]
 * @param {number}   [mandatoryBlockCount]
 */
export async function generateBlocksDirect(
  itineraryId,
  poiId,
  poiName,
  languages,
  depth = 'standard',
  tone = 'storytelling',
  poiInDepthDescription,
  comprehensiveDocUrl,
  ragManifestUrl,
  blockCount = 10,
  mandatoryBlockCount = 1
) {
  return callBlockAPI('/generate-blocks-direct', 'POST', {
    itineraryId,
    poiId,
    poiName,
    languages,
    depth,
    tone,
    poiInDepthDescription,
    comprehensiveDocUrl,
    ragManifestUrl,
    blockCount,
    mandatoryBlockCount,
  });
}

/**
 * Generate transition clips for all selected languages.
 *
 * @param {string}   itineraryId         - The itinerary identifier
 * @param {string[]} languages           - Language codes
 * @param {string}   [ragManifestUrl]    - Used to derive the storage path
 * @param {string[]} [customTransitions] - Optional custom transition texts
 */
export async function generateTransitions(itineraryId, languages, ragManifestUrl, customTransitions) {
  return callBlockAPI('/generate-transitions', 'POST', {
    itineraryId,
    languages,
    ragManifestUrl,
    ...(customTransitions ? { transitions: customTransitions.map(text => ({ text })) } : {}),
  });
}

/**
 * Build (or rebuild) the block_manifest.json for an itinerary.
 *
 * @param {string}   itineraryId        - The itinerary identifier
 * @param {Object[]} pois               - Array of POI config objects
 * @param {Object}   transitions        - Transitions keyed by language
 * @param {string[]} supportedLanguages - Languages in the manifest
 */
export async function buildManifest(itineraryId, pois, transitions, supportedLanguages, ragManifestUrl) {
  return callBlockAPI('/build-manifest', 'POST', {
    itineraryId,
    pois,
    transitions,
    supportedLanguages,
    ragManifestUrl,
  });
}

/**
 * Fetch the existing block manifest for an itinerary.
 */
export async function getManifest(itineraryId, ragManifestUrl) {
  const qs = ragManifestUrl ? `?ragManifestUrl=${encodeURIComponent(ragManifestUrl)}` : '';
  return callBlockAPI(`/manifest/${encodeURIComponent(itineraryId)}${qs}`, 'GET');
}

/**
 * Generate (or regenerate) TTS audio for a single block that already has a transcript.
 *
 * @param {string}  itineraryId
 * @param {string}  poiId
 * @param {string}  topic
 * @param {string}  transcript
 * @param {string}  language
 * @param {string}  [ragManifestUrl]
 * @param {string}  [voiceName]
 * @param {boolean} [useSimpleTts]
 * @param {string}  [voiceModel]
 */
export async function generateAudioForBlock(
  itineraryId,
  poiId,
  topic,
  transcript,
  language,
  ragManifestUrl,
  voiceName,
  useSimpleTts = false,
  voiceModel
) {
  return callBlockAPI('/generate-audio', 'POST', {
    itineraryId,
    poiId,
    topic,
    transcript,
    language,
    ragManifestUrl,
    voiceName,
    useSimpleTts,
    voiceModel,
  });
}

/**
 * Get per-POI block generation status for an itinerary.
 */
export async function getBlockStatus(itineraryId, ragManifestUrl) {
  const qs = ragManifestUrl ? `?ragManifestUrl=${encodeURIComponent(ragManifestUrl)}` : '';
  return callBlockAPI(`/status/${encodeURIComponent(itineraryId)}${qs}`, 'GET');
}
